
import Vue from 'vue';
import { namespace } from 'vuex-class';
import { Component, Emit, InjectReactive } from 'vue-property-decorator';

import { FilterCollection } from '@/stores/filter.store';
import WatchlistInnerTabNavigation from '@/components/watchlist/WatchlistInnerTabNavigation.vue';
import { CollectionType } from '@/enums/collection-type';
import { TabType } from '@/interfaces/inner-tabs';
import { CustomListsCountKey } from '@/constants/ListsCountProvider.vue';
import { DeclareUserStats, useUserStats } from '@/helpers/composables/useUserStats';
import { EmptyListSlideType } from '@/components/watchlist/empty-list-slider/empty-list-slider';
import { ListsCollectionType } from '@/stores/routing.store';

const Collections = () =>
	import(/* webpackChunkName: "collections" */ '@/components/watchlist/my-lists/Collections.vue');
const UserTitleList = () =>
	import(/* webpackChunkName: "user-titles-list" */ '@/components/watchlist/my-lists/UserTitleList.vue');
export const BasicButton = () => import(/* webpackChunkName: "basic-button" */ '@/ui-components/BasicButton.vue');
const EmptyListSlider = () =>
	import(/* webpackChunkName: "empty-list-slider" */ '@/components/watchlist/empty-list-slider/EmptyListSlider.vue');

const Filter = namespace('filter');
const User = namespace('user');
const Routing = namespace('routing');

@Component({
	name: 'MyLists',
	components: {
		EmptyListSlider,
		Collections,
		UserTitleList,
		WatchlistInnerTabNavigation,
		BasicButton,
	},
	setup() {
		const { customListsLimit } = useUserStats();
		return { customListsLimit };
	},
})
export default class MyLists extends Vue {
	@Filter.Getter(CollectionType.MY_LISTS) filterListsWatchlist: FilterCollection;
	@Routing.Action setLastActiveListsCollectionType: (lastActiveListsCollectionType: ListsCollectionType) => void;
	@User.Getter isLoggedIn: () => boolean;

	declare customListsLimit: DeclareUserStats['customListsLimit'];
	@InjectReactive(CustomListsCountKey) customListsCount: number;

	@Emit('scrollToTop')
	scrollToTop() {}

	created() {
		this.setLastActiveListsCollectionType(CollectionType.MY_LISTS);
	}

	get innerTabFilter() {
		return this.filterListsWatchlist.inner_tab || TabType.WATCHLIST;
	}

	get listsCount() {
		return !this.isLoggedIn() ? 0 : this.customListsCount;
	}

	get innerTabs() {
		return [
			{
				key: TabType.MY_LISTS,
				translation: this.$t('WEBAPP_LISTS_COUNT', {
					listsCount: `${this.listsCount}/${this.customListsLimit}`,
				}),
			},
			{
				key: TabType.WATCHLIST,
				translation: this.$tc('WEBAPP_WATCHLIST'),
			},
			{
				key: TabType.SEENLIST,
				translation: this.$tc('WEBAPP_SEEN'),
			},
			{
				key: TabType.LIKELIST,
				translation: this.$tc('WEBAPP_LISTS_LIKED'),
			},
		] as const;
	}

	get slides(): EmptyListSlideType[] {
		const useTVShowTrackingTranslation = this.$t('WEBAPP_LISTS_USE_TV_SHOW_TRACKING', {
			tvShowTracking: this.$t('WEBAPP_LISTS_TV_SHOW_TRACKING'),
		});

		return [
			{
				title: this.$tc('WEBAPP_LISTS_EMPTY_WATCHLIST_SLIDE_FIRST_TITLE'),
				message: this.$tc('WEBAPP_LISTS_EMPTY_WATCHLIST_SLIDE_FIRST_MESSAGE'),
				image: `/${ASSETS_DIR}/img/lists/placeholder-custom-lists.png`,
				image2x: `/${ASSETS_DIR}/img/lists/placeholder-custom-lists@2x.png`,
			},
			{
				title: this.$tc('WEBAPP_WATCHLIST'),
				message: this.$t('WEBAPP_LISTS_EMPTY_SHOW_WATCHLIST_MESSAGE', {
					useTvShowTracking: useTVShowTrackingTranslation,
				}) as string,
				image: `/${ASSETS_DIR}/img/lists/placeholder-watchlist.png`,
				image2x: `/${ASSETS_DIR}/img/lists/placeholder-watchlist@2x.png`,
			},
			{
				title: this.$tc('WEBAPP_LISTS_EMPTY_WATCHLIST_SLIDE_THIRD_TITLE'),
				message: this.$tc('WEBAPP_LISTS_EMPTY_WATCHLIST_SLIDE_THIRD_MESSAGE'),
				image: `/${ASSETS_DIR}/img/lists/placeholder-seen.png`,
				image2x: `/${ASSETS_DIR}/img/lists/placeholder-seen@2x.png`,
			},
		];
	}
}
